import { ComponentChildren, h, RefObject } from "preact";

import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";
import { SnapshotThumbnailSourceData } from "../../../foundation/utils/image";

import IconAI from "../Icons/IconAI";
import IconScreenVariant from "../Icons/IconScreenVariant";
import StatusIndicator from "../StatusIndicator";
import ThumbnailImage from "../ThumbnailImage";
import VariantIndicator from "../VariantIndicator";

import ScreenCardName from "./ScreenCardName";
import ScreenCardNameInput from "./ScreenCardNameInput";

import {
    SCREEN_CARD_WIDTH,
    SCREEN_CARD_THUMBNAIL_WIDTH
} from "./specs";

import styles from "./ScreenCard.module.css";

interface Props {
    class?: string;
    nameInputRef?: RefObject<HTMLTextAreaElement>;
    name: string;
    index?: number;
    thumbnailImageSource?: SnapshotThumbnailSourceData;
    thumbnailSource: string;
    thumbnailHeight: number;
    snapshotWidth?: number;
    snapshotHeight?: number;
    date?: ComponentChildren;
    filterValue?: string;
    statusIndicatorProps?: {
        name: string;
        color: string;
    } | null;
    variantCount?: number;
    shouldHighlightAsAIOrganized?: boolean;
    isAIOrganizedRejected?: boolean;
    isAIOrganizedVariantGroup?: boolean;
    isAIGeneratedVariantGroup?: boolean;
    highlightColor?: "blue" | "yellow";
    highlighted?: boolean;
    settingAsVariant?: boolean;
    isRenaming?: boolean;
    readOnly?: boolean;
    contextMenu?: ComponentChildren | null;
    onNameFocus?: (ev: Event) => void;
    onNameBlur?: (ev: FocusEvent) => void;
}

function ScreenCard({
    class: cssClass,
    nameInputRef,
    name,
    index,
    thumbnailImageSource,
    thumbnailSource,
    thumbnailHeight,
    snapshotWidth,
    snapshotHeight,
    date,
    filterValue,
    statusIndicatorProps,
    variantCount,
    shouldHighlightAsAIOrganized,
    isAIOrganizedRejected,
    isAIOrganizedVariantGroup,
    isAIGeneratedVariantGroup,
    highlightColor,
    highlighted,
    settingAsVariant,
    isRenaming,
    contextMenu,
    onNameFocus,
    onNameBlur
}: Props) {
    const style = {
        "--screen-card-width": `${SCREEN_CARD_WIDTH}px`,
        "--screen-card-thumbnail-width": `${SCREEN_CARD_THUMBNAIL_WIDTH}px`,
        "--screen-card-thumbnail-height": `${thumbnailHeight}px`
    } as CSSProperties;

    if (highlightColor) {
        style["--screen-card-highlight-color"] = `var(--marker-${highlightColor})`;
    }

    function renderSettingAsVariantIndicator() {
        if (!settingAsVariant) {
            return null;
        }

        return (
            <VariantIndicator
                class={classname(styles.variantIndicator, styles.settingAsVariantIndicator, {
                    [styles.AIOrganized]: isAIOrganizedVariantGroup,
                    [styles.AIOrganizedRejected]: isAIOrganizedRejected
                })}
                label={variantCount ? "Add as variants" : "Set as variants"}
                icon={<IconScreenVariant/>}/>
        );
    }

    function renderVariantCountIndicator() {
        if (variantCount === undefined) {
            return null;
        }

        return (
            <VariantIndicator
                class={classname(styles.variantIndicator, {
                    [styles.AIOrganized]: isAIOrganizedVariantGroup,
                    [styles.AIOrganizedRejected]: isAIOrganizedRejected
                })}
                label={variantCount + 1}
                icon={<IconScreenVariant/>}/>
        );
    }

    function renderStatusIndicator() {
        if (!statusIndicatorProps) {
            return null;
        }

        return (
            <StatusIndicator
                class={styles.statusIndicator}
                label={statusIndicatorProps.name}
                color={statusIndicatorProps.color}
                compact/>
        );
    }

    function renderAIOrganizedVariantIndicator() {
        if (!isAIOrganizedVariantGroup) {
            return null;
        }

        return (
            <div class={styles.AIOrganizedVariantIndicator}>
                <IconAI class={styles.AIOrganizedVariantIndicatorIcon}/>
                <p class={classname(styles.AIOrganizedVariantIndicatorText, "body-1")}>
                    {isAIGeneratedVariantGroup
                        ? "Screens combined as variant"
                        : "Screens added to variant group"}
                </p>
            </div>
        );
    }

    function renderName() {
        if (isRenaming) {
            return (
                <ScreenCardNameInput
                    inputRef={nameInputRef}
                    class={styles.nameInput}
                    value={name}
                    onFocus={onNameFocus}
                    onBlur={onNameBlur}/>
            );
        }

        return (
            <ScreenCardName
                class={styles.name}
                name={name}
                filterValue={filterValue}/>
        );
    }

    function renderDate() {
        if (!date) {
            return null;
        }

        return (
            <div class={classname("caption-1", styles.date)}>
                {date}
            </div>
        );
    }

    function renderContextMenu() {
        return contextMenu;
    }

    return (
        <div
            class={classname(styles.screenCard, cssClass, {
                [styles.highlighted]: highlighted,
                [styles.contextMenuOpen]: !!contextMenu,
                [styles.settingAsVariant]: settingAsVariant,
                [styles.highlightAsAIOrganized]: shouldHighlightAsAIOrganized,
                [styles.AIOrganizedRejected]: isAIOrganizedRejected
            })}
            style={style}>
            {renderSettingAsVariantIndicator()}
            <div class={styles.thumbnailWrapper}>
                {renderStatusIndicator()}
                <ThumbnailImage
                    index={index}
                    width={SCREEN_CARD_THUMBNAIL_WIDTH}
                    thumbnailImageSource={thumbnailImageSource}
                    snapshotWidth={snapshotWidth}
                    snapshotHeight={snapshotHeight}
                    height={thumbnailHeight}
                    source={thumbnailSource}/>
                {renderAIOrganizedVariantIndicator()}
            </div>
            <div class={styles.info}>
                {renderVariantCountIndicator()}
                {renderName()}
                {renderDate()}
            </div>
            <div class={styles.highlightOverlay}>
                <div class={styles.highlightBody}/>
            </div>
            {renderContextMenu()}
        </div>
    );
}

export default ScreenCard;
export { Props as ScreenCardProps };
